import { useState, useEffect } from 'react';
import { getSearchablePlanningApplicationText } from '../../lib/planning/searchText';

// Hook to get window size
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    
    // Add event listener
    window.addEventListener('resize', handleResize, { passive: true });
    
    // Call handler right away so state gets updated with initial window size
    handleResize();
    
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount and unmount
  
  return windowSize;
};

// Function to determine column count based on screen width
export const getColumnCount = (width) => {
  if (width.width >= 1280) return 3;  // XL screens: 3 columns
  if (width.width >= 1024) return 2;  // Desktop: 2 columns
  if (width.width >= 768) return 2;   // Tablets: 2 columns
  return 1;                          // Mobile: 1 column
};

// Filter options
export const filterOptions = [
  { value: 'all', label: 'All' },
  { value: 'recent', label: 'Recent' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'high_impact', label: 'High Impact' },
  { value: 'medium_impact', label: 'Medium Impact' },
  { value: 'low_impact', label: 'Low Impact' }
];

// Function to filter applications by status
export const filterApplicationsByStatus = (applications, status) => {
  if (!status || status === 'all') return applications;
  
  return applications.filter(app => {
    const appStatus = app.status?.toLowerCase() || '';
    
    switch(status) {
      case 'pending':
        return appStatus.includes('pending') || appStatus.includes('awaiting');
      case 'approved':
        return appStatus.includes('approved') || appStatus.includes('granted');
      case 'rejected':
        return appStatus.includes('rejected') || appStatus.includes('refused');
      case 'high_impact':
        return app.scale_rating >= 7;
      case 'medium_impact':
        return app.scale_rating >= 4 && app.scale_rating < 7;
      case 'low_impact':
        return app.scale_rating > 0 && app.scale_rating < 4;
      case 'recent': {
        // Check if validated_date or received_date is within the last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const validateDate = app.validated_date ? new Date(app.validated_date) : null;
        const receivedDate = app.received_date ? new Date(app.received_date) : null;
        
        return (validateDate && validateDate >= thirtyDaysAgo) || 
               (receivedDate && receivedDate >= thirtyDaysAgo);
      }
      default:
        return true;
    }
  });
};

// Function to sort applications by date
export const sortApplicationsByDate = (applications, ascending = false) => {
  return [...applications].sort((a, b) => {
    const dateA = a.validated_date || a.received_date || '';
    const dateB = b.validated_date || b.received_date || '';
    
    if (!dateA && !dateB) return 0;
    if (!dateA) return ascending ? -1 : 1;
    if (!dateB) return ascending ? 1 : -1;
    
    return ascending 
      ? new Date(dateA).getTime() - new Date(dateB).getTime()
      : new Date(dateB).getTime() - new Date(dateA).getTime();
  });
};

// Function to search applications
export const searchApplications = (applications, query) => {
  if (!query) return applications;
  
  const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);
  
  if (searchTerms.length === 0) return applications;
  
  return applications.filter(app => {
    const searchableFields = [
      getSearchablePlanningApplicationText(app),
      app.address,
      app.description,
      app.reference_no,
      app.status
    ].filter(Boolean).map(field => String(field).toLowerCase());
    
    // Check if any search term matches any field
    return searchTerms.some(term => 
      searchableFields.some(field => field.includes(term))
    );
  });
};
